import React from 'react';
import PageTransition from '../components/Shared/PageTransition';
import { useLanguage } from '../contexts/LanguageContext';

const SECTIONS = [
  {
    num: '01',
    badge: 'Scope & Purpose',
    badgeEn: 'Scope & Purpose',
    badgeColor: 'text-blue-400 bg-blue-500/10',
    title: 'Описание Сервиса и его Основная Цель',
    titleEn: 'Service Purpose & Scope',
    content: (
      <div className="space-y-3 text-sm sm:text-[15px] text-neutral-300 leading-relaxed font-normal">
        <p>
          1.1. Сервис StormHack — это комплекс профессиональных инструментов (включая RCON Audit, Account Checker, Bots Testing, DDoS Testing, Data Extraction, Servers Database, Secret Method и сопутствующие модули), созданный для проведения аудита безопасности и оценки устойчивости <strong className="text-white font-medium">исключительно тех серверов и инфраструктуры, которыми вы владеете, администрируете или на тестирование которых получили прямое письменное согласие правообладателей.</strong>
        </p>
        <p>
          1.2. <strong className="text-white font-medium">Сервис предназначен исключительно для легитимного и этичного использования.</strong> Любые попытки проведения несанкционированных атак, дестабилизации чужих вычислительных мощностей или неправомерного доступа категорически запрещены.
        </p>
        <p className="text-neutral-400 text-xs sm:text-sm">
          1.3. Команда StormHack непрерывно совершенствует платформу: функционал компонентов может оптимизироваться, расширяться или временно приостанавливаться для проведения регламентных технических работ.
        </p>
      </div>
    ),
    contentEn: (
      <div className="space-y-3 text-sm sm:text-[15px] text-neutral-300 leading-relaxed font-normal">
        <p>
          1.1. The StormHack service provides an advanced suite of security and resilience assessment tools (including RCON Audit, Account Checker, Bots Testing, DDoS Testing, Data Extraction, Servers Database, Secret Method, and auxiliary modules) engineered exclusively for auditing and evaluating <strong className="text-white font-medium">only those servers and network nodes that you own, administer, or have obtained explicit prior written authorization from lawful owners to test.</strong>
        </p>
        <p>
          1.2. <strong className="text-white font-medium">The Service is strictly dedicated to legitimate and ethical testing.</strong> Any attempts to execute unauthorized attacks, destabilize external computational infrastructure, or gain illegitimate access are strictly forbidden.
        </p>
        <p className="text-neutral-400 text-xs sm:text-sm">
          1.3. The StormHack team continuously develops the platform: module features may be enhanced, updated, or temporarily paused during scheduled technical maintenance.
        </p>
      </div>
    )
  },
  {
    num: '02',
    badge: 'Legal & Consent',
    badgeEn: 'Legal & Consent',
    badgeColor: 'text-emerald-400 bg-emerald-500/10',
    title: 'Ответственное Использование и Ваши Обязательства',
    titleEn: 'Responsible Use & User Obligations',
    content: (
      <div className="space-y-3 text-sm sm:text-[15px] text-neutral-300 leading-relaxed font-normal">
        <p>
          2.1. Вы обязуетесь применять Сервис и его функционал строго в законных целях и в точном соответствии с нормами действующего национального и международного законодательства.
        </p>
        <p>
          2.2. <strong className="text-white font-medium">Вы несете абсолютную и единоличную ответственность за получение всех необходимых документированных разрешений от владельцев целевых систем до инициации любых проверок.</strong>
        </p>
        <div className="mt-3.5 space-y-2">
          <p className="text-xs sm:text-sm text-neutral-400 font-medium">Категорически запрещается использовать Сервис для:</p>
          <ul className="list-disc list-inside space-y-1.5 text-xs sm:text-sm text-neutral-300 pl-1">
            <li>Любых действий, нарушающих нормы национального и международного права;</li>
            <li>Попыток несанкционированного проникновения в чужие системы или нарушения их штатной доступности;</li>
            <li>Распространения вредоносного ПО, ботнет-клиентов или несанкционированного спама;</li>
            <li>Нарушения прав интеллектуальной собственности и конфиденциальности третьих лиц.</li>
          </ul>
        </div>
      </div>
    ),
    contentEn: (
      <div className="space-y-3 text-sm sm:text-[15px] text-neutral-300 leading-relaxed font-normal">
        <p>
          2.1. You agree to utilize the Service and its capabilities strictly for lawful purposes and in precise compliance with applicable national and international legislation.
        </p>
        <p>
          2.2. <strong className="text-white font-medium">You bear sole and absolute responsibility for obtaining all necessary documented authorizations from system owners prior to initiating any security or stress test.</strong>
        </p>
        <div className="mt-3.5 space-y-2">
          <p className="text-xs sm:text-sm text-neutral-400 font-medium">Strictly prohibited activities include:</p>
          <ul className="list-disc list-inside space-y-1.5 text-xs sm:text-sm text-neutral-300 pl-1">
            <li>Any actions infringing domestic or international legal regulations;</li>
            <li>Attempts to compromise unauthorized systems or degrade standard operational availability;</li>
            <li>Distributing malicious code, trojans, botnet payloads, or unsolicited bulk communications;</li>
            <li>Violating intellectual property rights or data confidentiality of third parties.</li>
          </ul>
        </div>
      </div>
    )
  },
  {
    num: '03',
    badge: 'Limitations',
    badgeEn: 'Limitations',
    badgeColor: 'text-amber-400 bg-amber-500/10',
    title: 'Функциональность Сервиса и Понимание Ограничений',
    titleEn: 'Service Capabilities & System Limitations',
    content: (
      <div className="space-y-3 text-sm sm:text-[15px] text-neutral-300 leading-relaxed font-normal">
        <p>
          3.1. Сервис предоставляется по общепринятому принципу <strong className="text-white font-medium">«как есть» (as is)</strong>:
        </p>
        <ul className="list-disc list-inside space-y-1.5 text-xs sm:text-sm text-neutral-400 pl-1">
          <li>Результативность и точность аудита зависят от конфигурации, ядра и сетевых фильтров целевого сервера;</li>
          <li>Мы не гарантируем непрерывную работу в моменты технических работ или сбоев внешних каналов связи;</li>
          <li>Инструменты обладают высокой производительностью и требуют взвешенного обращения.</li>
        </ul>
      </div>
    ),
    contentEn: (
      <div className="space-y-3 text-sm sm:text-[15px] text-neutral-300 leading-relaxed font-normal">
        <p>
          3.1. The Service is provided on an <strong className="text-white font-medium">"as is" and "as available"</strong> standard:
        </p>
        <ul className="list-disc list-inside space-y-1.5 text-xs sm:text-sm text-neutral-400 pl-1">
          <li>Assessment effectiveness depends on target server software core, configuration, and upstream filtering policies;</li>
          <li>We do not guarantee uninterrupted availability during scheduled upgrades or upstream carrier transit issues;</li>
          <li>Platform utilities are high-throughput tools that require professional caution and deliberate operation.</li>
        </ul>
      </div>
    )
  },
  {
    num: '04',
    badge: 'Billing & Plans',
    badgeEn: 'Billing & Plans',
    badgeColor: 'text-purple-400 bg-purple-500/10',
    title: 'Тарифные Планы и Поддержка Инфраструктуры',
    titleEn: 'Subscription Plans & Infrastructure Support',
    content: (
      <div className="space-y-3 text-sm sm:text-[15px] text-neutral-300 leading-relaxed font-normal">
        <p>
          4.1. Базовый функционал доступен в рамках плана Free. Расширенные лимиты, специализированные методы и премиальные модули активируются по подписке (Vip, Premium, Professional).
        </p>
        <p className="text-neutral-400 text-xs sm:text-sm">
          4.2. Все платежи являются окончательными и расходуются на обеспечение серверных мощностей, пропускной способности каналов и развитие платформы.
        </p>
      </div>
    ),
    contentEn: (
      <div className="space-y-3 text-sm sm:text-[15px] text-neutral-300 leading-relaxed font-normal">
        <p>
          4.1. Core testing capabilities are accessible under the Free tier. Elevated throughput limits, exclusive audit methods, and specialized modules require an active subscription tier (Vip, Premium, Professional).
        </p>
        <p className="text-neutral-400 text-xs sm:text-sm">
          4.2. All payments are final and dedicated directly to cloud cluster capacity, dedicated bandwidth allocation, and ongoing platform development.
        </p>
      </div>
    )
  },
  {
    num: '05',
    badge: 'Intellectual Property',
    badgeEn: 'Intellectual Property',
    badgeColor: 'text-rose-400 bg-rose-500/10',
    title: 'Интеллектуальная Собственность',
    titleEn: 'Intellectual Property Rights',
    content: (
      <p className="text-sm sm:text-[15px] text-neutral-300 leading-relaxed font-normal">
        Все элементы платформы, алгоритмическая база, исходный код, графический интерфейс, фирменные наименования и сопроводительная документация являются исключительной интеллектуальной собственностью StormHack. Пользователю предоставляется ограниченная персональная лицензия на работу с интерфейсом.
      </p>
    ),
    contentEn: (
      <p className="text-sm sm:text-[15px] text-neutral-300 leading-relaxed font-normal">
        All platform architectures, algorithms, source code, UI designs, brand marks, and technical documentation constitute the exclusive intellectual property of StormHack. Users are granted a revocable, non-transferable personal license to interface with the platform.
      </p>
    )
  },
  {
    num: '06',
    badge: 'Liability Disclaimer',
    badgeEn: 'Liability Disclaimer',
    badgeColor: 'text-cyan-400 bg-cyan-500/10',
    title: 'Ограничение Ответственности и Возмещение Ущерба',
    titleEn: 'Limitation of Liability & Indemnity',
    content: (
      <div className="space-y-3 text-sm sm:text-[15px] text-neutral-300 leading-relaxed font-normal">
        <p>
          6.1. В максимальной степени, допускаемой применимым правом, разработчики и администрация StormHack не несут ответственности за любые прямые, косвенные убытки, упущенную выгоду или ущерб инфраструктуре, возникшие в результате неправомерных действий пользователя или тестирования систем без надлежащего согласия.
        </p>
        <p className="text-neutral-400 text-xs sm:text-sm">
          6.2. Пользователь безоговорочно соглашается оградить команду сервиса от любых исков, штрафов и претензий со стороны третьих лиц, вызванных нарушением условий настоящего Соглашения.
        </p>
      </div>
    ),
    contentEn: (
      <div className="space-y-3 text-sm sm:text-[15px] text-neutral-300 leading-relaxed font-normal">
        <p>
          6.1. To the maximum degree permitted under governing law, the creators and administrators of StormHack hold no liability for any direct or consequential damages, commercial loss, or operational downtime resulting from improper user conduct or audits initiated without legitimate verification.
        </p>
        <p className="text-neutral-400 text-xs sm:text-sm">
          6.2. The user unconditionally agrees to defend, indemnify, and hold harmless the platform team from any third-party claims, administrative penalties, or liabilities arising out of any breach of these Terms.
        </p>
      </div>
    )
  }
];

const TermsPage = () => {
  const { language } = useLanguage();
  const isEn = language === 'en';

  return (
    <PageTransition>
      <div className="min-h-screen text-white font-sans relative py-10 sm:py-16">
        <div className="max-w-[840px] mx-auto px-5 sm:px-6 relative z-10">
          
          {/* Header section with standalone shield icon and clean typography */}
          <div className="pb-8 sm:pb-10 border-b border-white/[0.08]">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-white/[0.08]">
              <div className="flex items-center gap-3.5">
                <svg
                  viewBox="0 0 24 24"
                  className="w-8 h-8 stroke-white fill-none shrink-0"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <polyline points="9 12 11 14 15 10" />
                </svg>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                    {isEn ? 'Terms of Service' : 'Пользовательское Соглашение'}
                  </h1>
                  <p className="text-xs text-white/45 font-mono mt-1">
                    StormHack Security Policy • {isEn ? 'Revision: Oct 13, 2025' : 'Редакция от 13.10.2025'}
                  </p>
                </div>
              </div>

              <span className="self-start sm:self-center px-3 py-1 rounded-full text-[11px] font-mono font-semibold tracking-wider uppercase bg-white/[0.05] text-white/70">
                Official Terms
              </span>
            </div>

            <p className="text-sm sm:text-base text-neutral-300 leading-relaxed mt-6">
              {isEn
                ? 'Welcome to StormHack. These Terms of Service govern your access to and use of our analytical and audit tools. By accessing or using the Service, you confirm your full and unconditional agreement with all provisions set forth in this document.'
                : 'Приветствуем в StormHack! Настоящее Соглашение регламентирует условия и порядок использования аналитических инструментов платформы. Используя Сервис, вы подтверждаете полное и безоговорочное согласие со всеми положениями данного документа.'}
            </p>
          </div>

          {/* Sections List without background boxes, styled identically to header */}
          <div>
            {SECTIONS.map((sec) => (
              <section
                key={sec.num}
                className="py-8 sm:py-10 border-b border-white/[0.08]"
              >
                <div className="flex items-center gap-2.5 mb-2.5">
                  <span className={`px-2 py-0.5 rounded-md font-mono font-bold text-xs ${sec.badgeColor} shrink-0`}>
                    {sec.num}
                  </span>
                  <span className="text-[11px] font-mono uppercase tracking-wider text-white/40">
                    {isEn ? sec.badgeEn : sec.badge}
                  </span>
                </div>
                <h2 className="text-lg sm:text-xl font-bold text-white tracking-tight mb-3">
                  {isEn ? sec.titleEn : sec.title}
                </h2>
                <div>
                  {isEn ? sec.contentEn : sec.content}
                </div>
              </section>
            ))}
          </div>

          {/* Strict Warning Banner without heavy container */}
          <div className="py-8 sm:py-10 border-b border-white/[0.08]">
            <div className="flex items-start gap-3.5 sm:gap-4">
              <svg
                viewBox="0 0 24 24"
                className="w-5 h-5 stroke-rose-400 fill-none shrink-0 mt-0.5"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <div className="text-xs sm:text-sm text-rose-200/90 leading-relaxed">
                <h3 className="font-bold text-rose-300 text-sm sm:text-base mb-1">
                  {isEn ? 'Strict Legal Warning' : 'Строгое предупреждение о правомерности'}
                </h3>
                <p>
                  {isEn
                    ? 'StormHack does not sell attack services («DDoS-for-hire»), does not distribute malware or botnets, and is strictly engineered for authorized internal audits of verified server infrastructure. Any other usage violates the law and will be prosecuted.'
                    : 'StormHack не осуществляет продажу услуг атак («DDoS-for-hire»), не распространяет вредоносный код и предназначен исключительно для закрытого аудита собственной инфраструктуры с согласия правообладателей. Любое иное использование нарушает закон и преследуется в установленном порядке.'}
                </p>
              </div>
            </div>
          </div>

          {/* Contacts & Support */}
          <div className="pt-10 sm:pt-12 pb-6 text-center flex flex-col items-center gap-1.5">
            <p className="text-xs text-white/40 uppercase font-mono tracking-wider">
              {isEn ? 'Questions regarding the terms?' : 'Вопросы по соглашению'}
            </p>
            <a
              href="mailto:stormhackinfo@gmail.com"
              className="text-white hover:text-white/80 font-bold text-base transition-colors hover:underline"
            >
              stormhackinfo@gmail.com
            </a>
          </div>

        </div>
      </div>
    </PageTransition>
  );
};

export default TermsPage;
